import React, { useEffect, useState } from 'react';
import { ExternalLink, ShieldCheck, X } from 'lucide-react';
import { pollOperatorLogin, startOperatorLogin } from './operatorAuthApi';

const svgDataUrl = (svg) => {
  const bytes = new TextEncoder().encode(svg);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return `data:image/svg+xml;base64,${window.btoa(binary)}`;
};

const OperatorLoginDialog = ({ onApproved, onClose }) => {
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let stopped = false;
    let timer;

    const begin = async () => {
      try {
        const started = await startOperatorLogin();
        if (stopped) return;
        setRequest(started);

        const poll = async () => {
          if (stopped) return;
          try {
            const result = await pollOperatorLogin(started.request_id);
            if (result.status === 'approved') {
              stopped = true;
              onApproved(result);
              return;
            }
            timer = window.setTimeout(poll, 1500);
          } catch (pollError) {
            if (pollError.response?.status === 202) {
              timer = window.setTimeout(poll, 1500);
              return;
            }
            setError(
              pollError.response?.data?.detail ||
              'Die Anmeldeanfrage ist abgelaufen. Bitte erneut öffnen.'
            );
          }
        };
        timer = window.setTimeout(poll, 1000);
      } catch (startError) {
        setError(
          startError.response?.data?.detail ||
          'Der Betreiberzugang ist momentan nicht erreichbar.'
        );
      }
    };

    begin();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [onApproved]);

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-8">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <button onClick={onClose} className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Schließen">
          <X className="h-5 w-5" />
        </button>
        <div className="mb-5 flex items-center gap-3 pr-10">
          <span className="rounded-xl bg-blue-50 p-3 text-blue-700"><ShieldCheck className="h-6 w-6" /></span>
          <div>
            <h2 className="text-xl font-bold text-gray-950">Betreiberzugang freigeben</h2>
            <p className="text-sm text-gray-600">Gültig für diese Kantinen-App, maximal 2 Stunden</p>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        ) : request ? (
          <div className="text-center">
            <img className="mx-auto h-64 w-64 max-w-full" src={svgDataUrl(request.qr_svg)} alt="QR-Code zur Betreiberfreigabe" />
            <p className="mt-4 font-medium text-gray-900">Mit dem iPhone scannen und per Face ID bestätigen</p>
            <a
              href={request.approval_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Auf diesem Gerät mit Face ID freigeben
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
            <p className="mt-2 text-sm text-gray-600">Prüfcode auf beiden Geräten: <strong className="tracking-wider text-gray-950">{request.verification_code}</strong></p>
            <p className="mt-4 text-xs text-gray-500">Diese Seite wartet automatisch auf die Bestätigung. Der QR-Code läuft nach kurzer Zeit ab.</p>
          </div>
        ) : (
          <div className="py-14 text-center text-gray-600">Sichere Anfrage wird erstellt …</div>
        )}
      </div>
    </div>
  );
};

export default OperatorLoginDialog;
